import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { fundListQueryOptions } from '@/features/mutual-funds/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { FUND_CATEGORIES, fundCategorySchema } from '@/schemas/mutual-funds'

/** `category` — the tab: Close-ended (default), Open-ended or Matured. */
export const priceVsNavSearchSchema = z.object({
  category: fundCategorySchema.default('close-ended').catch('close-ended'),
})
export type PriceVsNavSearch = z.infer<typeof priceVsNavSearchSchema>

/**
 * The `/mutual-funds/price-vs-nav` loader.
 *
 * MAIN: the directory for the entry URL's category — the whole page below
 * the tabs. Keyed on a search param, so the screen reads it with
 * `useFundList` (`keepPreviousData`), not suspense: a tab change dims the
 * old list instead of suspending the route (docs/route-loading.md, "Search
 * params").
 *
 * SECONDARY, and only for a real navigation: the OTHER two categories (six
 * and eight rows — cheap either way). Each category draws a different
 * layout — disclosure cards, or one of two table shapes — so
 * `keepPreviousData` alone would, for however long a tab click's fetch takes,
 * dim the RIGHT rows under the WRONG one (a click on Matured briefly drawing
 * active close-ended funds under a "Matured on" column). Warming both here
 * means a click almost always finds its category already cached;
 * `PriceVsNavScreen` still covers the rest of that window itself, by
 * tracking which category the rows on screen actually belong to.
 */
export async function loadPriceVsNav({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const { category } = priceVsNavSearchSchema.parse(location.search)
  if (!preload) {
    for (const other of FUND_CATEGORIES) {
      if (other !== category)
        prefetchRouteData(queryClient, fundListQueryOptions({ category: other }))
    }
  }
  await ensureRouteData(queryClient, fundListQueryOptions({ category }))
}
